import {
  Box,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Stack } from "@mui/material";
import React, { useState } from "react";
import {
  QRCodePng,
  appStorePng,
  googlePlayPng,
  facebookPng,
  instagramPng,
  twitterPng,
  linkedinPng,
} from "../../assets";
import SendIcon from "@mui/icons-material/Send";
import { MotionConfig, motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./Footer.css";

export const Footer = () => {
  const theme = useTheme();
  const is700 = useMediaQuery(theme.breakpoints.down(700));
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const labelStyles = {
    fontWeight: 300,
    cursor: "pointer",
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/subscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      setMessage(data.message || data.error);
      if (response.ok) {
        setEmail("");
      }
    } catch (error) {
      setMessage("Failed to subscribe. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.primary.main,
        paddingTop: "3rem",
        paddingLeft: is700 ? "1rem" : "3rem",
        paddingRight: is700 ? "1rem" : "3rem",
        paddingBottom: "1.5rem",
        rowGap: "5rem",
        color: theme.palette.primary.light,
        justifyContent: "space-around",
      }}
    >
      {/* upper */}
      <Stack
        flexDirection={"row"}
        rowGap={"1rem"}
        justifyContent={is700 ? "" : "space-around"}
        flexWrap={"wrap"}
      >
        <Stack rowGap={"1rem"} padding={"1rem"}>
          <Typography variant="h6" fontSize={"1.5rem"}>
            Exclusive
          </Typography>
          <Typography variant="h6">Subscribe</Typography>
          <Typography sx={labelStyles}>Get 10% off your first order</Typography>
          <form onSubmit={handleSubscribe}>
            <TextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              sx={{ border: "1px solid white", borderRadius: "6px" }}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SendIcon sx={{ color: theme.palette.primary.light }} />
                    )}
                  </IconButton>
                ),
                style: { color: "whitesmoke" },
              }}
            />
          </form>
          {message && (
            <Typography
              sx={{
                color: message.includes("successful") ? "green" : "error.main",
                fontSize: "0.875rem",
                mt: 1,
              }}
            >
              {message}
            </Typography>
          )}
        </Stack>

        <Stack rowGap={"1rem"} padding={"1rem"}>
          <Typography variant="h6">Support</Typography>
          <Typography sx={labelStyles}>11th Main Street, Accra-GH.</Typography>
          <Typography sx={labelStyles}>
            <a href="mailto:leslieajayi27@gmail.com" sx={labelStyles}>
              leslieajayi27@gmail.com
            </a>
          </Typography>
          <Typography sx={labelStyles}>
            <a href="tel:+233 27 123 7965" sx={labelStyles}>
              +233 27 123 7965
            </a>
          </Typography>
        </Stack>

        <Stack rowGap={"1rem"} padding={"1rem"}>
          <Typography variant="h6">Account</Typography>
          <Typography sx={labelStyles}>
            {" "}
            <a href="https://paltech-e-commerce.netlify.app/profile">
              My Account
            </a>{" "}
          </Typography>
          <Typography sx={labelStyles}>
            <a href="https://paltech-e-commerce.netlify.app/login">
              Login / Register
            </a>
          </Typography>
          <Typography sx={labelStyles}>
            <a href="https://paltech-e-commerce.netlify.app/cart"></a>
          </Typography>
          <Typography sx={labelStyles}>
            <a href="https://paltech-e-commerce.netlify.app/wishlist">
              {" "}
              Wishlist{" "}
            </a>
          </Typography>
          <Typography sx={labelStyles}>Shop</Typography>
        </Stack>

        <Stack rowGap={"1rem"} padding={"1rem"}>
          <Typography variant="h6">Quick Links</Typography>
          <Typography sx={labelStyles}>Privacy Policy</Typography>
          <Typography sx={labelStyles}>Terms Of Use</Typography>
          <Typography sx={labelStyles}>FAQ</Typography>
          <Typography sx={labelStyles}>Contact</Typography>
        </Stack>

        <Stack rowGap={"1rem"} padding={"1rem"}>
          <Typography variant="h6">Download App</Typography>
          <Typography
            sx={{ ...labelStyles, color: "graytext", fontWeight: 500 }}
          >
            Save $3 with App New User Only
          </Typography>
          <Stack flexDirection={"row"} columnGap={".5rem"}>
            <Box width={"100px"} height={"100px"}>
              <img
                src={QRCodePng}
                height={"100%"}
                width={"100%"}
                style={{ objectFit: "contain" }}
                alt="QR Code"
              />
            </Box>

            <Stack justifyContent={"space-around"}>
              <Stack>
                <img
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                  src={googlePlayPng}
                  alt="GooglePlay"
                />
              </Stack>
              <Stack>
                <img
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                  src={appStorePng}
                  alt="AppStore"
                />
              </Stack>
            </Stack>
          </Stack>

          <Stack mt={0.6} flexDirection={"row"} columnGap={"2rem"}>
            <MotionConfig whileHover={{ scale: 1.1 }} whileTap={{ scale: 1 }}>
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.img
                  style={{ cursor: "pointer" }}
                  src={facebookPng}
                  alt="Facebook"
                />
              </a>
              <a
                href="https://www.x.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.img
                  style={{ cursor: "pointer" }}
                  src={twitterPng}
                  alt="Twitter"
                />
              </a>
              <a
                href="https://instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.img
                  style={{ cursor: "pointer" }}
                  src={instagramPng}
                  alt="Instagram"
                />
              </a>
              <a
                href="https://linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.img
                  style={{ cursor: "pointer" }}
                  src={linkedinPng}
                  alt="LinkedIn"
                />
              </a>
            </MotionConfig>
          </Stack>
        </Stack>
      </Stack>

      {/* lower */}
      <Stack alignSelf={"center"}>
        <Typography color={"GrayText"}>
          &copy; P A L e-commmerce {new Date().getFullYear()}. All right
          reserved
        </Typography>
      </Stack>
    </Stack>
  );
};
